<template>
    <AppFormValidator
        ref="validator"
        #default="{ errors }"
        :model="model"
        :rules="rules">
        <CForm @submit.prevent>
            <CInput
                v-model="model.title"
                horizontal
                label="Название"
                placeholder="Введите название"
                :is-valid="errors.title && errors.title.isValid" />

            <CRow form class="form-group">
                <CCol
                    tag="label"
                    class="col-form-label"
                    sm="3">
                    Включено
                </CCol>
                <CCol sm="9">
                    <CSwitch
                        class=""
                        color="success"
                        shape="pill"
                        variant="opposite"
                        :checked.sync="model.enabled" />
                </CCol>
            </CRow>

            <!-- Блок автоматической АКТИВАЦИИ -->
            <CRow form class="form-group">
                <CCol
                    tag="label"
                    class="col-form-label"
                    sm="3">
                    Автоматическая активация
                </CCol>
                <CCol sm="9">
                    <CSwitch
                        class=""
                        color="success"
                        shape="pill"
                        variant="opposite"
                        :checked.sync="model.auto_activate" />
                </CCol>
            </CRow>

            <template v-if="model.auto_activate">
                <CSelect
                    horizontal
                    label="Условие активации"
                    v-model="model.auto_activate_condition"
                    :options="conditionOptions"
                    placeholder="Выберите условие" />

                <CInput
                    v-model.number="model.auto_activate_threshold"
                    horizontal
                    type="number"
                    label="Порог заказов для активации"
                    placeholder="Введите количество заказов"
                    min="0" />
            </template>

            <!-- Блок автоматической ДЕАКТИВАЦИИ -->
            <CRow form class="form-group">
                <CCol
                    tag="label"
                    class="col-form-label"
                    sm="3">
                    Автоматическая деактивация
                </CCol>
                <CCol sm="9">
                    <CSwitch
                        class=""
                        color="danger"
                        shape="pill"
                        variant="opposite"
                        :checked.sync="model.auto_deactivate" />
                </CCol>
            </CRow>

            <template v-if="model.auto_deactivate">
                <CSelect
                    horizontal
                    label="Условие деактивации"
                    v-model="model.auto_deactivate_condition"
                    :options="conditionOptions"
                    placeholder="Выберите условие" />

                <CInput
                    v-model.number="model.auto_deactivate_threshold"
                    horizontal
                    type="number"
                    label="Порог заказов для деактивации"
                    placeholder="Введите количество заказов"
                    min="0" />
            </template>

            <DemandShedule 
                v-if="entity.id"
                :entity-id="entity.id"
                :schedule-list="entity.schedules"
                @updated="$emit('schedule-updated')"
            />

            <div class="flex mt-4">
                <CButton color="primary" @click="submit">{{ submitButtonTitle }}
                </CButton>
                <CButton
                    v-if="model.id"
                    variant="outline"
                    color="danger"
                    class="ml-4"
                    @click="remove">Удалить
                </CButton>
            </div>
        </CForm>
    </AppFormValidator>
</template>

<script>
    import Vue from 'vue';
    import to from 'await-to-js';
    import { morphism } from 'morphism';
    import { requiredRule } from '@/utils/rules';
    import { BUSINESS_TYPE } from '@/utils/order';
    import { AUTHORIZATION_CHECK, CHECK_NEW_CLIENT } from '@/utils/popup-main-page';
    import { translit } from '@/utils/translit';
    import { cilCursorMove, cilTrash } from '@coreui/icons';
    import AppFormValidator from '@/components/AppFormValidator';
    import AppConfirmDialog from '@/components/AppConfirmDialog';
    import Draggable from 'vuedraggable';
    import ScheduleUpdate from '@/components/ScheduleUpdate';
    import Autocomplete from '@trevoreyre/autocomplete-vue';
    import '@trevoreyre/autocomplete-vue/dist/style.css';

    import DemandShedule from './DemandShedule'

    export default {
        components: {
            AppFormValidator,
            AppConfirmDialog,
            Draggable,
            ScheduleUpdate,
            Autocomplete,

            DemandShedule
        },
        props: {
            entity: {
                type: Object,
                default: () => ({}),
            },
        },

        data() {
            return {
                isAuthorized: false,
                model: {},
                schema: {
                    id: 'id',
                    title: 'title',
                    enabled: source => Boolean(source.enabled),
                    auto_activate: source => Boolean(source.auto_activate),
                    auto_activate_condition: 'auto_activate_condition',
                    auto_activate_threshold: 'auto_activate_threshold',
                    auto_deactivate: source => Boolean(source.auto_deactivate),
                    auto_deactivate_condition: 'auto_deactivate_condition',
                    auto_deactivate_threshold: 'auto_deactivate_threshold',
                    schedules: 'schedules'
                },
                rules: {
                    title: [requiredRule],
                    enabled: [requiredRule],
                },
                conditionOptions: [
                    { label: 'Больше или равно', value: 'more_than' },
                    { label: 'Строго меньше', value: 'less_than' }
                ],
            };
        },

        computed: {
            submitButtonTitle() {
                return typeof this.entity.id === 'number' ? 'Сохранить' : 'Создать';
            },
        },

        mounted(){
        },

        async created() {
            await this.setModel();
        },

        methods: {
            async setModel() {
                this.model = morphism(this.schema, this.entity);
            },
            async submit() {
                const [error] = await to(this.$refs.validator.validate());

                if (error) {
                    return false;
                }

                this.model.enabled = this.model.enabled ? true : false;
                this.model.auto_activate = this.model.auto_activate ? true : false;
                this.model.auto_deactivate = this.model.auto_deactivate ? true : false;

                // Если автоактивация выключена, очищаем связанные поля
                if (!this.model.auto_activate) {
                    this.model.auto_activate_condition = null;
                    this.model.auto_activate_threshold = null;
                }

                // Если автодеактивация выключена, очищаем связанные поля
                if (!this.model.auto_deactivate) {
                    this.model.auto_deactivate_condition = null;
                    this.model.auto_deactivate_threshold = null;
                }

                if (typeof this.model.id === 'number') {
                    const [err] = await to(
                        this.axios.put(`/demands/${this.model.id}`, this.model)
                    );
                    if (err) {
                        this.$root.$emit('showError', 'Ошибка. Не удалось сохранить запись. ' + err);
                        return;
                    }
                } else {
                    const [err] = await to(this.axios.post('/demands', this.model));
                    if (err) {
                        this.$root.$emit('showError', 'Ошибка. Не удалось создать новую запись. ' + err);
                        return;
                    }
                }

                this.$emit('success');
            },
            async remove() {
                const [err] = await to(this.axios.delete(`/demands/${this.model.id}`))
                if (err) {
                    this.$root.$emit('showError', 'Ошибка. Не удалось сохранить запись. ' + err);
                    return;
                }
                this.$emit('deleted')
            }
        },
    };
</script>
<style lang="scss">
    .demand-item-edit {
        &__product_title {
            display: flex;
            align-items: center;
            width: 100%;
            height: calc(1.5em + 0.75rem + 2px);
        }
        .autocomplete-input {
            width: 100%;
            height: calc(1.5em + 0.75rem + 2px);
            border: 1px solid #d8dbe0;
            border-radius: 0.25rem;
            padding: 0.375rem 0.75rem 0.375rem 2rem;
            background-color: #fff;
            background-image: url(data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIyNCIgaGVpZ2h0PSIyNCIgZmlsbD0ibm9uZSIgc3Ryb2tlPSIjNjY2IiBzdHJva2Utd2lkdGg9IjIiIHN0cm9rZS1saW5lY2FwPSJyb3VuZCIgc3Ryb2tlLWxpbmVqb2luPSJyb3VuZCI+PGNpcmNsZSBjeD0iMTEiIGN5PSIxMSIgcj0iOCIvPjxwYXRoIGQ9Ik0yMSAyMWwtNC00Ii8+PC9zdmc+);
            background-size: 0.875rem;
            background-position: left 0.75rem center;
            background-repeat: no-repeat;
            color: #768192;
            font-size: 0.875rem;
            font-weight: 400;
            line-height: 1.5;
        }
        .autocomplete-result {
            cursor: default;
            padding: 0.375rem 0.75rem 0.375rem 2rem;
            background-image: url(data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIyNCIgaGVpZ2h0PSIyNCIgZmlsbD0ibm9uZSIgc3Ryb2tlPSIjY2NjIiBzdHJva2Utd2lkdGg9IjIiIHN0cm9rZS1saW5lY2FwPSJyb3VuZCIgc3Ryb2tlLWxpbmVqb2luPSJyb3VuZCI+PGNpcmNsZSBjeD0iMTEiIGN5PSIxMSIgcj0iOCIvPjxwYXRoIGQ9Ik0yMSAyMWwtNC00Ii8+PC9zdmc+);
            background-size: 15px;
            background-position: left 0.75rem center;
            background-repeat: no-repeat;
            color: #768192;
            font-size: 0.875rem;
            font-weight: 400;
            line-height: 1.1;
        }
    }
</style>
